import React, { useRef, useState, useCallback, useEffect } from "react";
import "./Create.css";
import Menu from "../menu/Menu";

export default function Create({ onSubmit }) {
  useEffect(() => {
    document.title = "splash overflow | Create";
  }, []);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [contentItems, setContentItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [insertIndex, setInsertIndex] = useState(null);
  const [editingPosition, setEditingPosition] = useState(null);
  const [editingText, setEditingText] = useState("");
  const fileInputRef = useRef(null);
  const dragFileInputRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL;

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const dragAreaRef = useRef(null);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    imageFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedFiles((prev) => [
          ...prev,
          { file, name: file.name, preview: reader.result, type: file.type },
        ]);
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const [user, setUser] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/me`, { credentials: "include" })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => data && setUser(data));
  }, []);

  useEffect(() => {
    const dragArea = dragAreaRef.current;
    if (!dragArea) return;

    const preventDefaults = (e) => e.preventDefault();

    dragArea.addEventListener("dragover", preventDefaults);
    dragArea.addEventListener("drop", handleDrop);

    return () => {
      dragArea.removeEventListener("dragover", preventDefaults);
      dragArea.removeEventListener("drop", handleDrop);
    };
  }, [handleDrop]);

  const triggerFileSelect = (index = null) => {
    setInsertIndex(index);
    fileInputRef.current.click();
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file?.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const newImageItem = {
          type: "image",
          data: reader.result,
          textBefore: "",
          textAfter: "",
        };
        setContentItems((prev) => {
          const updated = [...prev];
          const idx = insertIndex === null ? updated.length : insertIndex;
          updated.splice(idx, 0, newImageItem);
          setCurrentIndex(idx);
          return updated;
        });
        setInsertIndex(null);
        setEditingText("");
        setEditingPosition(null);
        fileInputRef.current.value = "";
      };
      reader.readAsDataURL(file);
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);

      if (editingPosition && contentItems[newIndex]) {
        const newItem = contentItems[newIndex];
        const newText =
          editingPosition === "before"
            ? newItem.textBefore || ""
            : newItem.textAfter || "";
        setEditingText(newText);
      }
    }
    setEditingPosition(null);
  };

  const goToNext = () => {
    if (currentIndex < contentItems.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);

      if (editingPosition && contentItems[newIndex]) {
        const newItem = contentItems[newIndex];
        const newText =
          editingPosition === "before"
            ? newItem.textBefore || ""
            : newItem.textAfter || "";
        setEditingText(newText);
      }
    }
    setEditingPosition(null);
  };

  const handleDelete = () => {
    if (!contentItems.length) return;
    setContentItems((prev) => {
      const updated = [...prev];
      updated.splice(currentIndex, 1);
      setCurrentIndex((i) => Math.max(0, i - (i >= updated.length)));
      return updated;
    });
    setEditingPosition(null);
    setEditingText("");
  };

  const switchEditingPosition = (pos) => {
    const item = contentItems[currentIndex];
    if (!item) return;
    setEditingPosition(pos);
    setEditingText(
      pos === "before" ? item.textBefore || "" : item.textAfter || ""
    );
  };

  function dataURLtoFile(dataurl, filename) {
    const arr = dataurl.split(",");
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : "image/jpeg";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  const handleSubmit = async () => {
    const formData = new FormData();

    const contentTextData = contentItems.map(({ textBefore, textAfter }) => ({
      textBefore,
      textAfter,
    }));

    contentItems.forEach((item, index) => {
      const file = dataURLtoFile(item.data, `image-${index}.jpg`);
      formData.append("contentImages", file);
    });

    uploadedFiles.forEach((fileObj) => {
      if (fileObj.file) {
        formData.append("uploadedFiles", fileObj.file);
      }
    });

    formData.append("title", title);
    formData.append("description", description);
    formData.append("contentTextData", JSON.stringify(contentTextData));

    try {
      const res = await fetch(`${API_URL}/api/posts`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (res.ok) {
        alert("Post erfolgreich gespeichert");
        handleReset();
      } else {
        alert("Fehler beim Speichern in Create");
      }
    } catch (error) {
      console.error("Upload-Fehler:", error);
      alert("Netzwerkfehler beim Speichern");
    }
  };

  const handleReset = () => {
    setTitle("");
    setDescription("");
    setContentItems([]);
    setCurrentIndex(0);
    setEditingPosition(null);
    setEditingText("");
    setUploadedFiles([]);
  };

  const handleRemoveFile = (indexToRemove) => {
    const updatedFiles = uploadedFiles.filter((_, i) => i !== indexToRemove);
    setUploadedFiles(updatedFiles);
    console.log("Aktuelle Dateien:", updatedFiles);
  };

  const currentItem = contentItems[currentIndex];

  return (
    <div>
      <Menu user={user} setUser={setUser} />
      <div className="komplett">
        <div className="leftHalf">
          <div
            className={`a4box ${contentItems.length ? "image-present" : ""}`}
          >
            {contentItems.length ? (
              <>
                {currentIndex > 0 && (
                  <div className="arrowButton" onClick={goToPrevious}>
                    <span className="material-icons">keyboard_arrow_up</span>
                  </div>
                )}
                <div className="horizontal-line">
                  <span className="line" />
                  <span
                    className="icon material-icons"
                    title="Bild davor"
                    onClick={() => triggerFileSelect(currentIndex)}
                  >
                    add_photo_alternate
                  </span>
                  <span className="line" />
                  <span
                    className="icon material-icons"
                    title="Text davor"
                    onClick={() => switchEditingPosition("before")}
                  >
                    text_fields
                  </span>
                  <span className="line" />
                </div>
                <div className="flex items-center">
                  <div className="flex flex-column">
                    {currentItem &&
                      (editingPosition === "before" ||
                        !!currentItem.textBefore) && (
                        <div className="textBorder height-100 textareaImageDiv">
                          <textarea
                            className="transparentInput inter textareaImage"
                            placeholder="Text vor dem Bild..."
                            value={currentItem.textBefore}
                            onChange={(e) => {
                              const newVal = e.target.value;
                              setContentItems((prev) => {
                                const updated = [...prev];
                                const current = { ...updated[currentIndex] };
                                current.textBefore = newVal;
                                updated[currentIndex] = current;
                                return updated;
                              });
                            }}
                            rows={6}
                          />
                        </div>
                      )}

                    <img
                      src={currentItem.data}
                      alt={`Bild ${currentIndex + 1}`}
                      className="uploadedImage"
                    />

                    {currentItem &&
                      (editingPosition === "after" ||
                        !!currentItem.textAfter) && (
                        <div className="textBorder height-100 textareaImageDiv">
                          <textarea
                            className="transparentInput inter textareaImage"
                            placeholder="Text nach dem Bild..."
                            value={currentItem.textAfter}
                            onChange={(e) => {
                              const newVal = e.target.value;
                              setContentItems((prev) => {
                                const updated = [...prev];
                                const current = { ...updated[currentIndex] };
                                current.textAfter = newVal;
                                updated[currentIndex] = current;
                                return updated;
                              });
                            }}
                            rows={6}
                          />
                          {/* Formatierungs-Buttons 
                        <div className="formatting-buttons flex justify-end" style={{ marginBottom: '0.5em', gap: '0.5em' }}>
                          <span className="icon material-icons" title="irgendwas" onClick={() => formatSelection('**')}>format_bold</span>
                          <span className="icon material-icons" title="irgendwas" onClick={() => formatSelection('*')}>format_italic</span>
                          <span className="icon material-icons" title="irgendwas" onClick={() => formatSelection('[left]', '[/left]')}>align_horizontal_left</span>
                          <span className="icon material-icons" title="irgendwas" onClick={() => formatSelection('[center]', '[/center]')}>align_horizontal_center</span>
                          <span className="icon material-icons" title="irgendwas" onClick={() => formatSelection('[right]', '[/right]')}>align_horizontal_right</span>
                        </div>

                        {/* Vorschau 
                        <div
                          className="formattedPreview"
                          style={{ marginTop: '1em', whiteSpace: 'pre-wrap' }}
                          dangerouslySetInnerHTML={{ __html: formatText(currentItem.textAfter) }}
                        />*/}
                        </div>
                      )}
                  </div>

                  <div className="vl flex items-center" onClick={handleDelete}>
                    <span
                      className="material-icons"
                      style={{
                        position: "relative",
                        right: "0.5em",
                        backgroundColor: "white",
                      }}
                      title="Löschen"
                    >
                      delete
                    </span>
                  </div>
                </div>

                <div className="horizontal-line">
                  <span className="line" />
                  <span
                    className="icon material-icons"
                    title="Bild danach"
                    onClick={() => triggerFileSelect(currentIndex + 1)}
                  >
                    add_photo_alternate
                  </span>
                  <span className="line" />
                  <span
                    className="icon material-icons"
                    title="Text danach"
                    onClick={() => switchEditingPosition("after")}
                  >
                    text_fields
                  </span>
                  <span className="line" />
                </div>

                {currentIndex < contentItems.length - 1 && (
                  <div className="arrowButton" onClick={goToNext}>
                    <span className="material-icons">keyboard_arrow_down</span>
                  </div>
                )}
              </>
            ) : (
              <>
                <div
                  className="roundButton"
                  onClick={() => triggerFileSelect()}
                >
                  <span className="material-icons">add_photo_alternate</span>
                </div>
                <span style={{ margin: "0 5em 0 5em", textAlign: "center" }}>
                  Das oberste Bild deines Posts ist das Vorschaubild auf der
                  Startseite!
                </span>
              </>
            )}
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleImageUpload}
            />
          </div>
        </div>
        <div className="rightHalf">
          <input
            className="transparentInput textBorder inter"
            type="text"
            placeholder="Titel..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <textarea
            className="transparentInput textBorder inter"
            placeholder="Kurze Beschreibung über das gesamte Projekt..."
            rows={5}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <div className="flex items-center">
            <div
              ref={dragAreaRef}
              className="drag-drop-area flex flex-column items-center"
            >
              <p className="dragUploadText">
                Lade gerne zusätzliche Daten hoch, damit Nutzer dir praktisches
                Feedback geben können!
              </p>

              <input
                type="file"
                accept=".psd,.ai,.png,.jpeg,.jpg,.pdf"
                multiple
                style={{ display: "none" }}
                ref={dragFileInputRef}
                onChange={(e) => {
                  const allowedExtensions = [
                    ".psd",
                    ".ai",
                    ".png",
                    ".jpeg",
                    ".jpg",
                    ".pdf",
                  ];
                  const files = Array.from(e.target.files).filter((file) => {
                    const ext = file.name
                      .toLowerCase()
                      .slice(file.name.lastIndexOf("."));
                    if (!allowedExtensions.includes(ext)) {
                      alert(`Datei "${file.name}" ist nicht erlaubt.`);
                      return false;
                    }
                    return true;
                  });
                  const maxSize = 30 * 1024 * 1024;

                  const newFiles = [];

                  files.forEach((file) => {
                    if (file.size > maxSize) {
                      alert(`Datei "${file.name}" ist zu groß`);
                      return;
                    }

                    const isImage = file.type.startsWith("image/");
                    const reader = new FileReader();

                    reader.onloadend = () => {
                      newFiles.push({
                        file: file,
                        name: file.name,
                        preview: isImage ? reader.result : null,
                        type: file.type,
                      });

                      if (newFiles.length === files.length) {
                        setUploadedFiles((prev) => [...prev, ...newFiles]);
                      }
                    };

                    if (isImage) {
                      reader.readAsDataURL(file);
                    } else {
                      reader.onloadend();
                    }
                  });
                }}
              />

              <span
                className="icon material-icons"
                style={{ fontSize: "2.3em" }}
                title="Datei auswählen"
                onClick={() => dragFileInputRef.current?.click()}
              >
                upload_file
              </span>
            </div>

            {uploadedFiles.length > 0 && (
              <div className="preview-container">
                {uploadedFiles.map((file, index) => {
                  const isImage = file.preview !== null;

                  return (
                    <div
                      key={index}
                      style={{
                        position: "relative",
                        height: "10em",
                        border: "1px solid #ccc",
                        flexShrink: 0,
                      }}
                      onMouseEnter={(e) => {
                        const closeBtn =
                          e.currentTarget.querySelector(".remove-btn");
                        if (closeBtn) closeBtn.style.display = "block";
                      }}
                      onMouseLeave={(e) => {
                        const closeBtn =
                          e.currentTarget.querySelector(".remove-btn");
                        if (closeBtn) closeBtn.style.display = "none";
                      }}
                    >
                      {isImage ? (
                        <img
                          src={file.preview}
                          alt={file.name}
                          title={file.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            minWidth: "7em",
                            height: "100%",
                            maxWidth: "10em",
                          }}
                          className="flex items-center justify-center"
                        >
                          <span
                            style={{
                              fontSize: "0.75rem",
                              wordBreak: "break-word",
                              padding: "1em",
                            }}
                          >
                            {file.name}
                          </span>
                        </div>
                      )}

                      <button
                        className="remove-btn"
                        onClick={() => handleRemoveFile(index)}
                        style={{
                          display: "none",
                          position: "absolute",
                          top: "4px",
                          right: "4px",
                          backgroundColor: "rgba(0, 0, 0, 0.6)",
                          color: "#fff",
                          border: "none",
                          borderRadius: "50%",
                          width: "20px",
                          height: "20px",
                          cursor: "pointer",
                          fontSize: "14px",
                          lineHeight: "20px",
                          textAlign: "center",
                        }}
                        title="Datei entfernen"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="buttonRow">
            <button className="submitBtn button inter" onClick={handleSubmit}>
              Posten
            </button>
            <button className="resetBtn button inter" onClick={handleReset}>
              Abbrechen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
